import { useCallback, useEffect, useState } from 'react';
import { BuildingOffice2Icon, ClockIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import SmashLoader from '../../components/Loader/SmashLoader';
import {
  getOrganizations,
  patchOrganization,
} from '../../services/Universities/universitiesService';
import { useDebounce } from '../../utils/hooks/useDebounce';

const PAGE_SIZE = 12;
const TIME_BUDGET_MAX = 1800;
const MAX_COUNT_MAX = 15;

const limitsOf = (organization) => ({
  followup_time_budget_seconds: String(
    organization.followup_time_budget_seconds ?? '',
  ),
  followup_max_count: String(organization.followup_max_count ?? ''),
});

const validate = ({ followup_time_budget_seconds, followup_max_count }) => {
  const budget = Number(followup_time_budget_seconds);
  const count = Number(followup_max_count);
  if (!Number.isInteger(budget) || budget < 0 || budget > TIME_BUDGET_MAX) {
    return `Time budget must be a whole number between 0 and ${TIME_BUDGET_MAX}.`;
  }
  if (!Number.isInteger(count) || count < 0 || count > MAX_COUNT_MAX) {
    return `Max follow-ups must be a whole number between 0 and ${MAX_COUNT_MAX}.`;
  }
  return '';
};

export default function AdminOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingUuid, setSavingUuid] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const loadOrganizations = useCallback(
    (signal) => {
      setLoading(true);
      setError('');
      return getOrganizations({
        signal,
        params: {
          search: debouncedSearch || undefined,
          ordering: 'name',
          page,
          page_size: PAGE_SIZE,
        },
      })
        .then((data) => {
          const rows = data?.results || (Array.isArray(data) ? data : []);
          setOrganizations(rows);
          setCount(data?.count ?? rows.length);
          setDrafts(
            Object.fromEntries(rows.map((row) => [row.uuid, limitsOf(row)])),
          );
        })
        .catch((requestError) => {
          if (!signal?.aborted) {
            setError(
              requestError?.response?.data?.detail ||
                'Unable to load organizations.',
            );
          }
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [debouncedSearch, page],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadOrganizations(controller.signal);
    return () => controller.abort();
  }, [loadOrganizations]);

  useEffect(() => setPage(1), [debouncedSearch]);

  const setDraft = (uuid, field, value) =>
    setDrafts((previous) => ({
      ...previous,
      [uuid]: { ...previous[uuid], [field]: value },
    }));

  const isDirty = (organization) => {
    const draft = drafts[organization.uuid];
    if (!draft) return false;
    const saved = limitsOf(organization);
    return (
      draft.followup_time_budget_seconds !== saved.followup_time_budget_seconds ||
      draft.followup_max_count !== saved.followup_max_count
    );
  };

  const save = (organization) => {
    const draft = drafts[organization.uuid];
    const validationError = validate(draft);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSavingUuid(organization.uuid);
    patchOrganization(organization.uuid, {
      followup_time_budget_seconds: Number(draft.followup_time_budget_seconds),
      followup_max_count: Number(draft.followup_max_count),
    })
      .then((updated) => {
        setOrganizations((previous) =>
          previous.map((row) =>
            row.uuid === organization.uuid ? { ...row, ...updated } : row,
          ),
        );
        toast.success(`Limits saved for ${organization.name}`);
      })
      .catch((requestError) =>
        toast.error(
          requestError?.response?.data?.detail ||
            'Limits could not be saved. Platform administrator access is required.',
        ),
      )
      .finally(() => setSavingUuid(''));
  };

  const reset = (organization) =>
    setDrafts((previous) => ({
      ...previous,
      [organization.uuid]: limitsOf(organization),
    }));

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const inputClass =
    'w-24 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

  return (
    <div className="mx-auto w-full max-w-7xl space-y-7 px-4 py-8 sm:px-6 lg:px-8">
      <header>
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">
          Platform administration
        </p>
        <h1 className="mt-2 text-3xl font-bold text-gray-950 dark:text-white">
          Organizations
        </h1>
        <p className="mt-2 max-w-3xl text-gray-600 dark:text-gray-300">
          Set how far the AI may probe in each organization&apos;s interviews.
          The follow-up chain stops when either limit is reached; 0 disables
          follow-up questions entirely. Only platform administrators can change
          these values.
        </p>
      </header>

      <section
        aria-label="Organization filters"
        className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
      >
        <label className="block text-sm font-medium">
          Search
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, city or country"
            className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900 md:max-w-md"
          />
        </label>
      </section>

      {error && (
        <div
          role="alert"
          className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800"
        >
          <p>{error}</p>
          <button
            onClick={() => loadOrganizations()}
            className="mt-3 font-semibold underline"
          >
            Try again
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex min-h-64 items-center justify-center">
          <SmashLoader />
        </div>
      ) : organizations.length ? (
        <section aria-label="Organization limits" className="space-y-4">
          {organizations.map((organization) => {
            const draft = drafts[organization.uuid] || limitsOf(organization);
            const dirty = isDirty(organization);
            const saving = savingUuid === organization.uuid;
            return (
              <article
                key={organization.uuid}
                className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:flex-row lg:items-end lg:justify-between"
              >
                <div className="min-w-0">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-950 dark:text-white">
                    <BuildingOffice2Icon className="h-5 w-5 text-blue-600" />
                    {organization.name}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {[organization.city, organization.country]
                      .filter(Boolean)
                      .join(', ') || 'Location not set'}
                  </p>
                </div>

                <div className="flex flex-wrap items-end gap-4">
                  <label className="text-sm font-medium">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="h-4 w-4 text-gray-400" />
                      Time budget (s)
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={TIME_BUDGET_MAX}
                      value={draft.followup_time_budget_seconds}
                      onChange={(event) =>
                        setDraft(
                          organization.uuid,
                          'followup_time_budget_seconds',
                          event.target.value,
                        )
                      }
                      aria-label={`Follow-up time budget for ${organization.name}`}
                      className={`mt-1 block ${inputClass}`}
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Max follow-ups
                    <input
                      type="number"
                      min={0}
                      max={MAX_COUNT_MAX}
                      value={draft.followup_max_count}
                      onChange={(event) =>
                        setDraft(
                          organization.uuid,
                          'followup_max_count',
                          event.target.value,
                        )
                      }
                      aria-label={`Max follow-up questions for ${organization.name}`}
                      className={`mt-1 block ${inputClass}`}
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!dirty || saving}
                      onClick={() => save(organization)}
                      className="rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      disabled={!dirty || saving}
                      onClick={() => reset(organization)}
                      className="rounded-xl border border-gray-300 px-4 py-2.5 font-semibold disabled:opacity-40 dark:border-gray-600"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        !error && (
          <section className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-xl font-bold">No organizations found</h2>
            <p className="mt-2 text-gray-500">
              Adjust the search to find the organization you want to configure.
            </p>
          </section>
        )
      )}

      {!loading && !error && count > PAGE_SIZE && (
        <nav
          aria-label="Organization pagination"
          className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
        >
          <button
            disabled={page === 1}
            onClick={() => setPage((value) => value - 1)}
            className="rounded-lg border px-4 py-2 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages} · {count} organizations
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border px-4 py-2 disabled:opacity-40"
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
}
